import { useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { Tables } from "@/integrations/supabase/types"

type Profile = Tables<"profiles">

interface EditUserData {
  id: string
  full_name?: string
  email?: string
  password?: string
  role?: Profile['role']
  condominium_id?: string | null
  is_active?: boolean
}

export const useEditUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userData: EditUserData) => {
      const { id, full_name, email, password, role, condominium_id, is_active } = userData

      // Primeiro, atualizar dados do perfil (sem email - email é tratado separadamente)
      const profileUpdates: Partial<Profile> = {}
      if (full_name !== undefined) profileUpdates.full_name = full_name
      if (role !== undefined) profileUpdates.role = role
      if (condominium_id !== undefined) profileUpdates.condominium_id = condominium_id
      if (is_active !== undefined) profileUpdates.is_active = is_active

      // Atualizar perfil se há dados para atualizar
      if (Object.keys(profileUpdates).length > 0) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update(profileUpdates)
          .eq("id", id)

        if (profileError) {
          throw new Error(`Erro ao atualizar perfil: ${profileError.message}`)
        }
      }

      // Atualizar email se fornecido - via Edge Function primeiro, depois profiles
      if (email) {
        const { data: session } = await supabase.auth.getSession()
        
        if (!session?.session?.access_token) {
          throw new Error('Usuário não autenticado')
        }

        const { data, error } = await supabase.functions.invoke('update-user-email', {
          body: { userId: id, newEmail: email },
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
          },
        })

        if (error || data?.error) {
          throw new Error(data?.error || 'Erro ao atualizar email')
        }

        // Edge Function já atualiza profiles.email, mas garantir sincronização
        await supabase.from("profiles").update({ email }).eq("id", id)
      }

      // Atualizar senha se fornecida
      if (password) {
        const { data: session } = await supabase.auth.getSession()
        
        if (!session?.session?.access_token) {
          throw new Error('Usuário não autenticado')
        }

        const { data, error } = await supabase.functions.invoke('change-user-password', {
          body: { userId: id, newPassword: password },
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
          },
        })

        if (error || data?.error) {
          throw new Error(data?.error || 'Erro ao atualizar senha')
        }
      }

      return { success: true }
    },
    onSuccess: () => {
      toast.success("Usuário atualizado com sucesso!")
      // Invalidar cache dos perfis para recarregar a lista
      queryClient.invalidateQueries({ queryKey: ["profiles"] })
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar usuário: ${error.message}`)
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data: session } = await supabase.auth.getSession()
      
      if (!session?.session?.access_token) {
        throw new Error('Usuário não autenticado')
      }

      // Primeiro, desativar o usuário na tabela profiles
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ is_active: false })
        .eq("id", userId)

      if (profileError) {
        throw new Error(`Erro ao desativar usuário: ${profileError.message}`)
      }

      return { success: true }
    },
    onSuccess: () => {
      toast.success("Usuário desativado com sucesso!")
      queryClient.invalidateQueries({ queryKey: ["profiles"] })
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover usuário: ${error.message}`)
    },
  })
}